import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, Input, Checkbox } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';
import { apiClient } from '../../api/apiConfig';
import { Spinner } from '../ui';
import { BudgetGroup, BudgetCategoryConfig } from '../../types/finance';

interface Category {
  id: number;
  name: string;
}

interface BudgetSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const FREQUENCY_OPTIONS = [
  { value: '1', label: 'Monthly' },
  { value: '2', label: 'Every 2 months' },
  { value: '3', label: 'Quarterly' },
  { value: '6', label: 'Every 6 months' },
  { value: '12', label: 'Annually' },
];

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const BudgetSetup: React.FC<BudgetSetupProps> = ({ isOpen, onClose, onSaved }) => {
  const [groups, setGroups] = useState<BudgetGroup[]>([]);
  const [configs, setConfigs] = useState<BudgetCategoryConfig[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // New config form
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newGroupId, setNewGroupId] = useState('');
  const [newFrequency, setNewFrequency] = useState('1');
  const [newRollover, setNewRollover] = useState(false);
  const [newAmount, setNewAmount] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const [grpRes, cfgRes, catRes] = await Promise.all([
          apiClient.get('/api/budget-groups/'),
          apiClient.get('/api/budget-category-configs/'),
          apiClient.get('/api/categories/?type=budget'),
        ]);
        setGroups(grpRes.data.results || grpRes.data);
        setConfigs(cfgRes.data.results || cfgRes.data);
        setCategories(catRes.data.results || catRes.data);
      } catch (err) {
        console.error('Failed to fetch budget setup data', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [isOpen]);

  const configuredCategoryIds = new Set(configs.map(c => c.category));
  const unconfiguredCategories = categories.filter(c => !configuredCategoryIds.has(c.id));

  const handleAddConfig = async () => {
    if (!newCategoryId || !newGroupId) return;
    setSaving(true);
    try {
      await apiClient.post('/api/budget-category-configs/', {
        category: parseInt(newCategoryId),
        budget_group: parseInt(newGroupId),
        frequency_months: parseInt(newFrequency),
        rollover_enabled: newRollover,
        default_budgeted_amount: parseFloat(newAmount) || 0,
      });
      // Refresh
      const res = await apiClient.get('/api/budget-category-configs/');
      setConfigs(res.data.results || res.data);
      setNewCategoryId('');
      setNewGroupId('');
      setNewFrequency('1');
      setNewRollover(false);
      setNewAmount('');
    } catch (err) {
      console.error('Failed to add config', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/api/budget-category-configs/${id}/`);
      setConfigs(configs.filter(c => c.id !== id));
    } catch (err) {
      console.error('Failed to delete config', err);
    }
  };

  const handleClose = () => {
    onSaved();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Budget Category Setup"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={handleClose}>Done</Button>
        </div>
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[24] }}>
          <Spinner size="md" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          {/* Existing configs */}
          <div>
            <div style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              marginBottom: SPACING[8],
            }}>
              Configured Categories ({configs.length})
            </div>
            {configs.length === 0 ? (
              <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>
                No categories configured yet. Add one below.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
                {configs.map(cfg => (
                  <div key={cfg.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: `${SPACING[4]} ${SPACING[8]}`,
                    backgroundColor: COLORS.bg.hover,
                    borderRadius: '4px',
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                      <span style={{ fontWeight: '500', color: COLORS.text.primary }}>{cfg.category_name}</span>
                      <span style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.tiny }}>
                        → {cfg.budget_group_name}
                      </span>
                      <span style={{ color: COLORS.text.tertiary, fontSize: TYPOGRAPHY.body.sizes.tiny }}>
                        {cfg.frequency_display}
                      </span>
                      {cfg.rollover_enabled && (
                        <span style={{ color: COLORS.accent.default, fontSize: TYPOGRAPHY.body.sizes.tiny }}>rollover</span>
                      )}
                      <span style={{ color: COLORS.text.secondary, fontSize: TYPOGRAPHY.body.sizes.tiny }}>
                        {formatCurrency(cfg.default_budgeted_amount)}
                      </span>
                    </div>
                    <Button variant="ghost" onClick={() => handleDelete(cfg.id)} style={{ color: COLORS.status.error, padding: SPACING[4] }}>
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add new config */}
          <div style={{
            borderTop: `1px solid ${COLORS.border.default}`,
            paddingTop: SPACING[16],
          }}>
            <div style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              marginBottom: SPACING[8],
            }}>
              Add Category
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[8] }}>
              <Select
                value={newCategoryId}
                onChange={(e) => setNewCategoryId(e.target.value)}
              >
                <option value="">Select category...</option>
                {unconfiguredCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
              <Select
                value={newGroupId}
                onChange={(e) => setNewGroupId(e.target.value)}
              >
                <option value="">Select group...</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </Select>
              <Select
                value={newFrequency}
                onChange={(e) => setNewFrequency(e.target.value)}
              >
                {FREQUENCY_OPTIONS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </Select>
              <Input
                type="number"
                placeholder="Default amount"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12], marginTop: SPACING[8] }}>
              <Checkbox
                checked={newRollover}
                onChange={(e) => setNewRollover(e.target.checked)}
              />
              <span style={{ fontSize: TYPOGRAPHY.body.sizes.default, color: COLORS.text.secondary }}>
                Enable rollover (carry unused budget to next month)
              </span>
              <div style={{ flex: 1 }} />
              <Button
                variant="primary"
                onClick={handleAddConfig}
                disabled={!newCategoryId || !newGroupId || saving}
              >
                {saving ? 'Adding...' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BudgetSetup;
